import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export type AssignmentStatus = 'open' | 'completed' | 'cancelled'

export interface PracticeAssignment {
  id: string
  skillId: string | null
  skillName: string | null
  instructions: string
  status: AssignmentStatus
  dueDate: string | null
  createdAt: string
  completedAt: string | null
}

interface CreateAssignmentResult {
  ok: boolean
  code?: string
  assignmentId?: string
}

interface UsePracticeAssignmentsReturn {
  assignments: PracticeAssignment[]
  loading: boolean
  error: string | null
  saving: boolean
  createAssignment: (instructions: string, skillId: string | null, dueDate: string | null) => Promise<CreateAssignmentResult>
  closeAssignment: (assignmentId: string, status: 'completed' | 'cancelled') => Promise<{ ok: boolean }>
  refresh: () => void
}

/**
 * Practice assignments for one instructor-student relationship (migration 062).
 * Authorization for creation is re-verified entirely server-side inside
 * create_practice_assignment() — this hook never asserts ownership itself. Reads/updates
 * of existing assignments rely on practice_assignments' own RLS (instructor_id =
 * auth.uid(), AND an active relationship for updates — closing one out is blocked once
 * the relationship has ended, same as driving_lessons after migration 061).
 */
export function usePracticeAssignments(relationshipId: string | undefined): UsePracticeAssignmentsReturn {
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<PracticeAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchAssignments = useCallback(async () => {
    if (!user || !relationshipId) return
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchErr } = await supabase
        .from('practice_assignments')
        .select('id, skill_id, instructions, status, due_date, created_at, completed_at, driving_skills(name)')
        .eq('relationship_id', relationshipId)
        .order('created_at', { ascending: false })

      if (fetchErr) { setError('Could not load practice assignments.'); return }

      setAssignments((data ?? []).map((row: any) => ({
        id: row.id,
        skillId: row.skill_id,
        skillName: row.driving_skills?.name ?? null,
        instructions: row.instructions,
        status: row.status,
        dueDate: row.due_date,
        createdAt: row.created_at,
        completedAt: row.completed_at,
      })))
    } finally {
      setLoading(false)
    }
  }, [user, relationshipId])

  useEffect(() => { fetchAssignments() }, [fetchAssignments])

  const createAssignment = useCallback(async (
    instructions: string,
    skillId: string | null,
    dueDate: string | null,
  ): Promise<CreateAssignmentResult> => {
    if (!relationshipId) return { ok: false, code: 'no_relationship' }
    setSaving(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('create_practice_assignment', {
        p_relationship_id: relationshipId,
        p_instructions: instructions,
        p_skill_id: skillId ?? undefined,
        p_due_date: dueDate ?? undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string; assignment_id?: string } | null
      if (result?.ok) await fetchAssignments()
      return { ok: result?.ok ?? false, code: result?.code, assignmentId: result?.assignment_id }
    } finally {
      setSaving(false)
    }
  }, [relationshipId, fetchAssignments])

  const closeAssignment = useCallback(async (
    assignmentId: string,
    status: 'completed' | 'cancelled',
  ): Promise<{ ok: boolean }> => {
    setSaving(true)
    try {
      const { error: updateErr } = await supabase
        .from('practice_assignments')
        .update({ status })
        .eq('id', assignmentId)

      if (updateErr) return { ok: false }
      await fetchAssignments()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [fetchAssignments])

  return { assignments, loading, error, saving, createAssignment, closeAssignment, refresh: fetchAssignments }
}
